/**
 * Oprávnění uživatele jsou uložený profil v `user_permission_profiles`.
 *
 * Jediný zdroj role → výchozí práva: `seedPermissionProfileFromLegacyAccess`
 * (alias `permissionProfileFromRoles`). Použití:
 * - seed při založení účtu / chybějící řádek
 * - datová migrace / backfill prázdných profilů
 * - klik na role chip (uživatel, pozvánka, strop firmy) přepíše draft seedem z vybraných rolí
 * - pozvánka uloží tento draft; registrace ho zkopíruje a z rolí znovu neseeduje
 *
 * Více rolí = sjednocení (union) přes legacy role helpery. Deselect role
 * přepočítá profil z zbývajících vybraných rolí (nesmaže ruční úpravy mimo tento tok —
 * v UI vždy přepíše draft celým seedem z vybraných rolí).
 *
 * Uložení účtu ukládá draft oprávnění zvlášť; změna role v API profil nepřepočítává.
 */

import {
  canAccessAuthorityBremenaAgenda,
  canAccessAuthorityPrepAgenda,
  canViewAuthorityEmailMailbox,
  canViewAuthorityIsdsMailbox,
} from './authority.constants';
import { canEditNavolavani, canViewNavolavaniMenu } from './navolavani.constants';
import {
  ASSIGNABLE_ROLE_OPTIONS,
  ROLES,
  canAccessFakturace,
  canAccessImportMenu,
  canAccessPdChangesNotes,
  canAccessWorkReportMenu,
  canDeleteMapObjects,
  canViewOtherUsersActivityAndWorkReport,
  hasAdminOrSuperAdminRole,
  hasConstructionDiaryAccessRole,
  hasEquipmentAccessRole,
  hasEquipmentEditRole,
  hasObjectContentMutatorRole,
  hasOrgAdminRole,
  hasSuperAdminRole,
  hasWarehouseEditRole,
  sanitizeCompanyRoleGrants,
} from './roles.constants';

export const PERMISSION_ACTIONS = ['view', 'edit', 'delete'] as const;
export type PermissionAction = (typeof PERMISSION_ACTIONS)[number];

export interface AppPermissionSectionDef {
  id: string;
  label: string;
  group: string;
}

/** Id záložek odpovídají menu ve webovém shellu a sekcím Uživatelé / Firmy. */
export const APP_PERMISSION_SECTIONS = [
  { id: 'dashboard', label: 'Dashboard', group: 'Management' },
  { id: 'fakturace', label: 'Fakturace', group: 'Management' },
  { id: 'fronta-prace', label: 'Fronta práce', group: 'Management' },
  { id: 'work-report', label: 'Výkaz práce', group: 'Management' },
  { id: 'maps', label: 'Mapy', group: 'Obecné' },
  { id: 'polygons', label: 'Polygony', group: 'Obecné' },
  { id: 'objects', label: 'Objekty', group: 'Obecné' },
  { id: 'diary', label: 'Stavební deník', group: 'Realizace' },
  { id: 'navolavani', label: 'Navolávání', group: 'Realizace' },
  { id: 'inzenyring', label: 'Inženýring', group: 'Příprava' },
  { id: 'priprava', label: 'Příprava staveb', group: 'Příprava' },
  { id: 'email', label: 'Email', group: 'Příprava' },
  { id: 'datovka', label: 'Datovka', group: 'Příprava' },
  { id: 'pd-changes', label: 'Změny oproti PD', group: 'Příprava' },
  { id: 'users', label: 'Uživatelé', group: 'HR' },
  { id: 'users-companies', label: 'Firmy', group: 'HR' },
  { id: 'users-checks', label: 'Kontroly', group: 'HR' },
  { id: 'users-invites', label: 'Pozvánky', group: 'HR' },
  { id: 'users-company-settings', label: 'Nastavení firmy', group: 'HR' },
  { id: 'user-permissions', label: 'Oprávnění uživatelů', group: 'HR' },
  { id: 'materials', label: 'Sklady', group: 'Logistika' },
  { id: 'equipment', label: 'Zařízení', group: 'Logistika' },
  { id: 'import', label: 'Import', group: 'Dev' },
  { id: 'database', label: 'Databáze', group: 'Dev' },
  { id: 'app-settings', label: 'Nastavení aplikace', group: 'Účet' },
] as const satisfies readonly AppPermissionSectionDef[];

export type AppPermissionSectionId = (typeof APP_PERMISSION_SECTIONS)[number]['id'];

export const APP_PERMISSION_SECTION_IDS: readonly AppPermissionSectionId[] = APP_PERMISSION_SECTIONS.map(
  (section) => section.id,
);

export interface SectionPermission {
  view: boolean;
  edit: boolean;
  delete: boolean;
}

export interface UserPermissionOrganizations {
  /** Konkrétní firmy, jejich uživatele smí vidět. Prázdné + bez „všechny“ = jen vlastní firma, pokud ji jde určit. */
  viewCompanyIds: string[];
  /**
   * Firmy, které smí spravovat (pozvánky, nastavení).
   * Prázdný seznam a `manageAllCompanies: false` = nespravuje cizí firmy.
   * Vlastní `primaryCompanyId` členství tím není.
   */
  manageCompanyIds: string[];
  viewAllCompanies: boolean;
  manageAllCompanies: boolean;
}

export interface UserPermissionProfile {
  sections: Record<AppPermissionSectionId, SectionPermission>;
  organizations: UserPermissionOrganizations;
}

export interface LegacyAccessSeedInput {
  roles?: string[] | null;
  /** Domovská firma v okamžiku seedu (`users.primary_company_id`). */
  companyId?: string | null;
}

function flags(view: boolean, edit = false, remove = false): SectionPermission {
  return {
    view,
    edit: view && edit,
    delete: view && edit && remove,
  };
}

function allGranted(): SectionPermission {
  return { view: true, edit: true, delete: true };
}

function emptyOrganizations(): UserPermissionOrganizations {
  return {
    viewCompanyIds: [],
    manageCompanyIds: [],
    viewAllCompanies: false,
    manageAllCompanies: false,
  };
}

function stringList(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  const out: string[] = [];
  for (const item of value) {
    const id = String(item ?? '').trim();
    if (!id || out.includes(id)) continue;
    out.push(id);
  }
  return out;
}

export function emptyPermissionProfile(): UserPermissionProfile {
  const sections = {} as Record<AppPermissionSectionId, SectionPermission>;
  for (const id of APP_PERMISSION_SECTION_IDS) {
    sections[id] = { view: false, edit: false, delete: false };
  }
  return { sections, organizations: emptyOrganizations() };
}

function sectionFlagsFromRaw(row: unknown): SectionPermission {
  const flagsIn = row && typeof row === 'object' ? (row as Record<string, unknown>) : {};
  return {
    view: flagsIn['view'] === true,
    edit: flagsIn['edit'] === true,
    delete: flagsIn['delete'] === true,
  };
}

/** Doplní chybějící sekce jako vypnuté. Neznámá id zahodí. Neodvozuje práva z role. */
export function normalizeUserPermissionProfile(raw: unknown): UserPermissionProfile {
  const source = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const sectionsRaw = source['sections'];
  const sectionsIn =
    sectionsRaw && typeof sectionsRaw === 'object'
      ? (sectionsRaw as Record<string, unknown>)
      : {};
  const base = emptyPermissionProfile();
  for (const id of APP_PERMISSION_SECTION_IDS) {
    base.sections[id] = sectionFlagsFromRaw(sectionsIn[id]);
  }
  // Legacy single „Komunikace“ → Email + Datovka when the new keys were not stored yet.
  if (Object.prototype.hasOwnProperty.call(sectionsIn, 'komunikace')) {
    const legacy = sectionFlagsFromRaw(sectionsIn['komunikace']);
    if (!Object.prototype.hasOwnProperty.call(sectionsIn, 'email')) {
      base.sections.email = { ...legacy };
    }
    if (!Object.prototype.hasOwnProperty.call(sectionsIn, 'datovka')) {
      base.sections.datovka = { ...legacy };
    }
  }
  const orgsRaw = source['organizations'];
  const orgsIn =
    orgsRaw && typeof orgsRaw === 'object'
      ? (orgsRaw as Record<string, unknown>)
      : {};
  base.organizations = {
    viewCompanyIds: stringList(orgsIn['viewCompanyIds']),
    manageCompanyIds: stringList(orgsIn['manageCompanyIds']),
    viewAllCompanies: orgsIn['viewAllCompanies'] === true,
    manageAllCompanies: orgsIn['manageAllCompanies'] === true,
  };
  return base;
}

/**
 * Prázdný objekt (tabulka profilů ještě není, nebo se nenačetla).
 * Uložený profil s aspoň jedním příznakem unset není.
 * Samotná role přístup neotvírá — prázdný profil není oprávnění.
 */
export function isUnsetPermissionProfile(profile: UserPermissionProfile | null | undefined): boolean {
  if (!profile) return true;
  const sections = Object.values(profile.sections ?? {});
  if (sections.some((flags) => flags?.view || flags?.edit || flags?.delete)) return false;
  const orgs = profile.organizations;
  if (!orgs) return true;
  return !orgs.viewAllCompanies
    && !orgs.manageAllCompanies
    && (orgs.viewCompanyIds?.length ?? 0) === 0
    && (orgs.manageCompanyIds?.length ?? 0) === 0;
}

/**
 * Modul Oprávnění. Rozhoduje uložený příznak `user-permissions.edit`.
 * Druhý argument zůstává kvůli volajícím; role superadmin sama přístup nedává.
 */
export function canEditUserPermissionProfiles(rawProfile: unknown, _isSuperAdmin = false): boolean {
  return permissionAllows(rawProfile, 'user-permissions', 'edit');
}

export function hasPermission(
  profile: UserPermissionProfile | null | undefined,
  section: AppPermissionSectionId,
  action: PermissionAction,
): boolean {
  const flagsForSection = profile?.sections?.[section];
  if (!flagsForSection) return false;
  return flagsForSection[action] === true;
}

/**
 * Uložený profil. Prázdný / chybějící profil vrací null — to není oprávnění.
 * Role se sem nečte; předvyplnění je jen `seedPermissionProfileFromLegacyAccess`.
 */
export function loadedPermissionProfile(raw: unknown): UserPermissionProfile | null {
  if (raw == null) return null;
  const profile = normalizeUserPermissionProfile(raw);
  return isUnsetPermissionProfile(profile) ? null : profile;
}

/**
 * Jediná brána přístupu: uložený příznak sekce.
 * Chybějící profil i vypnutý příznak vrací false. Argument role se nepoužívá.
 */
export function permissionAllows(
  rawProfile: unknown,
  section: AppPermissionSectionId,
  action: PermissionAction,
): boolean {
  const profile = loadedPermissionProfile(rawProfile);
  if (!profile) return false;
  return hasPermission(profile, section, action);
}

/** Menu / route `/equipment`. Číst = `sections.equipment.view`. Role se ignoruje. */
export function canViewEquipmentSection(
  _roles: string[] | null | undefined,
  rawProfile: unknown,
): boolean {
  return permissionAllows(rawProfile, 'equipment', 'view');
}

/** Úpravy Zařízení. Editovat = `sections.equipment.edit`. Role se ignoruje. */
export function canEditEquipmentSection(
  _roles: string[] | null | undefined,
  rawProfile: unknown,
): boolean {
  return permissionAllows(rawProfile, 'equipment', 'edit');
}

/** Odstraňování Zařízení. Odstraňovat = `sections.equipment.delete`. Role se ignoruje. */
export function canDeleteEquipmentSection(
  _roles: string[] | null | undefined,
  rawProfile: unknown,
): boolean {
  return permissionAllows(rawProfile, 'equipment', 'delete');
}

/** Menu / route `/materials`. Číst = `sections.materials.view`. Role se ignoruje. */
export function canViewMaterialsSection(
  _roles: string[] | null | undefined,
  rawProfile: unknown,
): boolean {
  return permissionAllows(rawProfile, 'materials', 'view');
}

/** Úpravy Skladů. Editovat = `sections.materials.edit`. Role se ignoruje. */
export function canEditMaterialsSection(
  _roles: string[] | null | undefined,
  rawProfile: unknown,
): boolean {
  return permissionAllows(rawProfile, 'materials', 'edit');
}

/** Odstraňování Skladů. Odstraňovat = `sections.materials.delete`. Role se ignoruje. */
export function canDeleteMaterialsSection(
  _roles: string[] | null | undefined,
  rawProfile: unknown,
): boolean {
  return permissionAllows(rawProfile, 'materials', 'delete');
}

/**
 * Záložka hubu Úřady → sekce, kterou editor už má.
 * `nastaveni` chip nemá (připojení schránky) — vrací false, ať guard zůstane u své kontroly.
 */
export function canViewAuthorityHubTab(rawProfile: unknown, tab: string): boolean {
  switch (tab) {
    case 'prehled':
    case 'kontakty':
      return (
        permissionAllows(rawProfile, 'priprava', 'view')
        || permissionAllows(rawProfile, 'inzenyring', 'view')
      );
    case 'posta':
      return permissionAllows(rawProfile, 'email', 'view');
    case 'krizeni-siti':
    case 'vytyceni':
    case 'povinnosti':
      return permissionAllows(rawProfile, 'priprava', 'view');
    case 'datovka':
    case 'k-schvaleni':
      return permissionAllows(rawProfile, 'datovka', 'view');
    case 'bremena':
    case 'dotcene-organy':
      return permissionAllows(rawProfile, 'inzenyring', 'view');
    default:
      return false;
  }
}

/**
 * Seznam firem (záložka Firmy): explicitní sekce, příznak všech firem,
 * nebo víc než jedna konkrétní firma v profilu.
 */
export function canOpenCompanyList(profile: UserPermissionProfile | null | undefined): boolean {
  if (!profile) return false;
  if (hasPermission(profile, 'users-companies', 'view')) return true;
  if (profile.organizations.viewAllCompanies || profile.organizations.manageAllCompanies) return true;
  const ids = new Set([
    ...profile.organizations.viewCompanyIds,
    ...profile.organizations.manageCompanyIds,
  ]);
  return ids.size > 1;
}

export function canManageCompany(
  profile: UserPermissionProfile | null | undefined,
  companyId: string | null | undefined,
): boolean {
  const id = String(companyId ?? '').trim();
  if (!profile || !id) return false;
  if (profile.organizations.manageAllCompanies) return true;
  return profile.organizations.manageCompanyIds.includes(id);
}

/**
 * `'all'` = všechny firmy tenantu.
 * Prázdné pole = žádná firma (adresář nesmí spadnout na všechny uživatele).
 * Když profil neuvádí id, použije se `ownCompanyId` (domovská firma).
 */
export function visibleCompanyIds(
  profile: UserPermissionProfile | null | undefined,
  ownCompanyId?: string | null,
): string[] | 'all' {
  if (!profile) return [];
  if (profile.organizations.viewAllCompanies || profile.organizations.manageAllCompanies) return 'all';
  const own = String(ownCompanyId ?? '').trim();
  return stringList([
    ...profile.organizations.viewCompanyIds,
    ...profile.organizations.manageCompanyIds,
    ...(own ? [own] : []),
  ]);
}

/**
 * Výchozí profil z rolí (historické RBAC → sekce Číst/Editovat/Odstraňovat).
 * Multi-role: union práv. Stejná funkce pro seed, migraci i UI chip.
 */
export function seedPermissionProfileFromLegacyAccess(input: LegacyAccessSeedInput): UserPermissionProfile {
  const roles = input.roles ?? [];
  const companyId = String(input.companyId ?? '').trim();
  if (hasSuperAdminRole(roles)) {
    const sections = {} as Record<AppPermissionSectionId, SectionPermission>;
    for (const id of APP_PERMISSION_SECTION_IDS) sections[id] = allGranted();
    return {
      sections,
      organizations: {
        viewCompanyIds: [],
        manageCompanyIds: [],
        viewAllCompanies: true,
        manageAllCompanies: true,
      },
    };
  }

  const directory = canViewOtherUsersActivityAndWorkReport(roles) || hasOrgAdminRole(roles);
  const oversight = canViewOtherUsersActivityAndWorkReport(roles);
  const orgAdmin = hasOrgAdminRole(roles);
  const mutator = hasObjectContentMutatorRole(roles);
  const adminSettings = hasAdminOrSuperAdminRole(roles);
  const own = companyId ? [companyId] : [];

  const sections: Record<AppPermissionSectionId, SectionPermission> = {
    dashboard: flags(true, false, false),
    fakturace: flags(canAccessFakturace(roles), true, false),
    'fronta-prace': flags(true, mutator, false),
    'work-report': flags(canAccessWorkReportMenu(roles), true, false),
    maps: flags(true, mutator, false),
    polygons: flags(true, mutator, false),
    objects: flags(true, mutator, canDeleteMapObjects(roles)),
    diary: flags(hasConstructionDiaryAccessRole(roles), true, false),
    navolavani: flags(canViewNavolavaniMenu(roles), canEditNavolavani(roles), false),
    inzenyring: flags(canAccessAuthorityBremenaAgenda(roles), true, false),
    priprava: flags(canAccessAuthorityPrepAgenda(roles), true, false),
    email: flags(canViewAuthorityEmailMailbox(roles), true, false),
    datovka: flags(canViewAuthorityIsdsMailbox(roles), true, false),
    'pd-changes': flags(canAccessPdChangesNotes(roles), true, false),
    users: flags(directory, orgAdmin, false),
    'users-companies': flags(false, false, false),
    'users-checks': flags(oversight, false, false),
    'users-invites': flags(orgAdmin, orgAdmin, orgAdmin),
    'users-company-settings': flags(false, false, false),
    'user-permissions': flags(false, false, false),
    materials: flags(true, hasWarehouseEditRole(roles), false),
    equipment: flags(hasEquipmentAccessRole(roles), hasEquipmentEditRole(roles), false),
    import: flags(canAccessImportMenu(roles), true, false),
    database: flags(false, false, false),
    'app-settings': flags(adminSettings, true, false),
  };

  return {
    sections,
    organizations: {
      viewCompanyIds: own,
      manageCompanyIds: orgAdmin && companyId ? [companyId] : [],
      viewAllCompanies: false,
      manageAllCompanies: false,
    },
  };
}

/** Alias — stejný výsledek jako `seedPermissionProfileFromLegacyAccess`. */
export function permissionProfileFromRoles(input: LegacyAccessSeedInput): UserPermissionProfile {
  return seedPermissionProfileFromLegacyAccess(input);
}

function permissionProfilesEqual(a: UserPermissionProfile, b: UserPermissionProfile): boolean {
  for (const id of APP_PERMISSION_SECTION_IDS) {
    const left = a.sections[id];
    const right = b.sections[id];
    if (left.view !== right.view || left.edit !== right.edit || left.delete !== right.delete) return false;
  }
  const leftOrgs = a.organizations;
  const rightOrgs = b.organizations;
  if (leftOrgs.viewAllCompanies !== rightOrgs.viewAllCompanies) return false;
  if (leftOrgs.manageAllCompanies !== rightOrgs.manageAllCompanies) return false;
  if (leftOrgs.viewCompanyIds.join('\0') !== rightOrgs.viewCompanyIds.join('\0')) return false;
  if (leftOrgs.manageCompanyIds.join('\0') !== rightOrgs.manageCompanyIds.join('\0')) return false;
  return true;
}

/**
 * Role, která oproti prázdnému výběru zapne nějaké oprávnění.
 * Prohlížeč a Akvizice seedují stejný základ jako žádná role — chip by nic nepředvyplnil.
 */
export function roleSeedsPermissionPreset(role: string): boolean {
  const baseline = seedPermissionProfileFromLegacyAccess({ roles: [], companyId: null });
  const seeded = seedPermissionProfileFromLegacyAccess({ roles: [role], companyId: null });
  return !permissionProfilesEqual(baseline, seeded);
}

/** Přiřaditelné role, jejichž klik na chip opravdu předvyplní mřížku oprávnění. */
export const PERMISSION_PRESET_ROLE_OPTIONS: readonly { id: string; label: string }[] =
  ASSIGNABLE_ROLE_OPTIONS.filter((option) => roleSeedsPermissionPreset(option.id));

/**
 * Profil z pozvánky při registraci.
 * Uložený profil se použije tak, jak je. Chybí-li (staré pozvánky), platí seed z rolí.
 */
export function resolveRegistrationPermissionProfile(input: {
  stored?: unknown;
  roles?: string[] | null;
  companyId?: string | null;
}): UserPermissionProfile {
  if (input.stored != null) return normalizeUserPermissionProfile(input.stored);
  return seedPermissionProfileFromLegacyAccess({
    roles: input.roles,
    companyId: input.companyId ?? null,
  });
}

export type UserPermissionProfileBackfillDecision =
  | { action: 'keep' }
  | { action: 'insert'; profile: UserPermissionProfile };

/**
 * Jednorázový backfill `user_permission_profiles`.
 * Uložený řádek se nemění — vypnutý chip zůstane vypnutý, i když by ho role znovu zapnula.
 * Chybějící řádek se doplní seedem z rolí (`seedPermissionProfileFromLegacyAccess`),
 * protože právě ten přístup runtime dával, dokud profil neexistoval.
 * Po insertu druhé spuštění vidí řádek a vrátí `keep`.
 */
export function planUserPermissionProfileBackfill(input: {
  /** `true` = řádek v `user_permission_profiles` už existuje (včetně prázdného / vypnutého profilu). */
  hasStoredProfile: boolean;
  roles?: string[] | null;
  companyId?: string | null;
}): UserPermissionProfileBackfillDecision {
  if (input.hasStoredProfile) return { action: 'keep' };
  return {
    action: 'insert',
    profile: seedPermissionProfileFromLegacyAccess({
      roles: input.roles,
      companyId: input.companyId ?? null,
    }),
  };
}

/**
 * Sekce staveb, u kterých subdodavatel nesmí nikdy dostat právo `delete`
 * (polygony, objekty, stavební deník, navolávání, změny oproti PD).
 */
export const STAVBY_DELETE_FORBIDDEN_SECTION_IDS: readonly AppPermissionSectionId[] = [
  'polygons',
  'objects',
  'diary',
  'navolavani',
  'pd-changes',
] as const;

/**
 * Systémové sekce, které správce subdodavatelské firmy nesmí delegovat svým uživatelům.
 */
export const COMPANY_DELEGATION_FORBIDDEN_SECTION_IDS: readonly AppPermissionSectionId[] = [
  'database',
  'users-companies',
  'app-settings',
] as const;

export interface CompanyPermissionCeilingInput {
  /**
   * Explicitní strop oprávnění firmy (preferovaný). Stejný tvar jako u uživatele.
   * Když chybí / je prázdný, použije se legacy `grantedRoles`.
   */
  companyProfile?: UserPermissionProfile | null;
  /** Legacy: sjednocení práv z povolených rolí firmy. */
  grantedRoles?: readonly string[] | null;
  companyId: string;
  callerProfile?: UserPermissionProfile | null;
  /** Ponechá výchozí org_admin sekce (users, users-invites), pokud cílový účet sám drží org_admin. */
  includeOrgAdminSelf?: boolean;
}

/**
 * Uložený strop firmy: normalizace + pevná pravidla (bez unionu s callerem / rolemi).
 * Použití při zápisu company permission profile.
 */
export function normalizeCompanyDefaultPermissionProfile(
  raw: unknown,
  companyId: string,
): UserPermissionProfile {
  return applyCompanyDelegationHardRules(normalizeUserPermissionProfile(raw), companyId);
}

function applyCompanyDelegationHardRules(
  profile: UserPermissionProfile,
  companyIdRaw: string,
): UserPermissionProfile {
  const companyId = String(companyIdRaw ?? '').trim();
  const sections = {} as Record<AppPermissionSectionId, SectionPermission>;
  for (const id of APP_PERMISSION_SECTION_IDS) {
    sections[id] = { ...profile.sections[id] };
  }

  for (const id of STAVBY_DELETE_FORBIDDEN_SECTION_IDS) {
    sections[id] = { ...sections[id], delete: false };
  }
  for (const id of COMPANY_DELEGATION_FORBIDDEN_SECTION_IDS) {
    sections[id] = { view: false, edit: false, delete: false };
  }
  sections['users-company-settings'] = {
    view: sections['users-company-settings'].view,
    edit: sections['users-company-settings'].edit,
    delete: false,
  };
  sections['user-permissions'] = {
    view: sections['user-permissions'].view,
    edit: sections['user-permissions'].edit,
    delete: false,
  };

  return {
    sections,
    organizations: {
      viewCompanyIds: companyId ? [companyId] : [],
      manageCompanyIds: companyId ? [companyId] : [],
      viewAllCompanies: false,
      manageAllCompanies: false,
    },
  };
}

function unionSectionFlags(
  a: SectionPermission | undefined,
  b: SectionPermission | undefined,
): SectionPermission {
  return {
    view: Boolean(a?.view || b?.view),
    edit: Boolean(a?.edit || b?.edit),
    delete: Boolean(a?.delete || b?.delete),
  };
}

/**
 * Vypočítá strop oprávnění (delegation ceiling) pro subdodavatelskou firmu / správce firmy:
 * - preferuje uložený `companyProfile`; jinak sjednocení z `grantedRoles` (legacy)
 * - sjednocení s profilem volajícího (`callerProfile`)
 * - vždy zakázáno `delete` na stavbách a globální sekce; org rozsah zamčený na `companyId`
 */
export function buildCompanyPermissionCeiling(input: CompanyPermissionCeilingInput): UserPermissionProfile {
  const companyId = String(input.companyId ?? '').trim();
  const fromCompany =
    input.companyProfile && !isUnsetPermissionProfile(input.companyProfile)
      ? normalizeUserPermissionProfile(input.companyProfile)
      : null;

  const granted = sanitizeCompanyRoleGrants(
    input.grantedRoles ? [...input.grantedRoles] : [],
  );
  const rolePool = input.includeOrgAdminSelf ? [...granted, ROLES.ORG_ADMIN] : granted;
  const fromRoles = seedPermissionProfileFromLegacyAccess({
    roles: rolePool,
    companyId: companyId || null,
  });

  let base = fromCompany ?? fromRoles;
  if (fromCompany && input.includeOrgAdminSelf) {
    const orgAdminSeed = seedPermissionProfileFromLegacyAccess({
      roles: [ROLES.ORG_ADMIN],
      companyId: companyId || null,
    });
    const sections = {} as Record<AppPermissionSectionId, SectionPermission>;
    for (const id of APP_PERMISSION_SECTION_IDS) {
      sections[id] = unionSectionFlags(base.sections[id], orgAdminSeed.sections[id]);
    }
    base = { ...base, sections };
  }

  const caller =
    input.callerProfile && !isUnsetPermissionProfile(input.callerProfile)
      ? normalizeUserPermissionProfile(input.callerProfile)
      : null;

  const sections = {} as Record<AppPermissionSectionId, SectionPermission>;
  for (const id of APP_PERMISSION_SECTION_IDS) {
    sections[id] = unionSectionFlags(base.sections[id], caller?.sections[id]);
  }

  return applyCompanyDelegationHardRules({ sections, organizations: base.organizations }, companyId);
}

/**
 * Vrátí seznam porušení stropu oprávnění firmy (prázdné pole = profil je v povoleném rozsahu).
 */
export function findPermissionCeilingViolations(
  requestedRaw: unknown,
  ceiling: UserPermissionProfile,
): string[] {
  const requested = normalizeUserPermissionProfile(requestedRaw);
  const violations: string[] = [];

  for (const id of APP_PERMISSION_SECTION_IDS) {
    const reqFlags = requested.sections[id];
    const capFlags = ceiling.sections[id];
    for (const action of PERMISSION_ACTIONS) {
      if (reqFlags[action] && !capFlags?.[action]) {
        violations.push(`${id}.${action}`);
      }
    }
  }

  if (requested.organizations.viewAllCompanies && !ceiling.organizations.viewAllCompanies) {
    violations.push('organizations.viewAllCompanies');
  }
  if (requested.organizations.manageAllCompanies && !ceiling.organizations.manageAllCompanies) {
    violations.push('organizations.manageAllCompanies');
  }

  const allowedView = new Set(ceiling.organizations.viewCompanyIds);
  for (const cid of requested.organizations.viewCompanyIds) {
    if (!allowedView.has(cid)) {
      violations.push(`organizations.viewCompanyIds:${cid}`);
    }
  }

  const allowedManage = new Set(ceiling.organizations.manageCompanyIds);
  for (const cid of requested.organizations.manageCompanyIds) {
    if (!allowedManage.has(cid)) {
      violations.push(`organizations.manageCompanyIds:${cid}`);
    }
  }

  return violations;
}

/**
 * Ořízne požadovaný profil na strop oprávnění firmy (průnik práv + zákaz mazání staveb).
 */
export function clampPermissionProfileToCeiling(
  requestedRaw: unknown,
  ceiling: UserPermissionProfile,
): UserPermissionProfile {
  const requested = normalizeUserPermissionProfile(requestedRaw);
  const sections = {} as Record<AppPermissionSectionId, SectionPermission>;

  for (const id of APP_PERMISSION_SECTION_IDS) {
    const reqFlags = requested.sections[id];
    const capFlags = ceiling.sections[id];
    const view = Boolean(reqFlags.view && capFlags?.view);
    const edit = Boolean(view && reqFlags.edit && capFlags?.edit);
    const remove = Boolean(
      view &&
        edit &&
        reqFlags.delete &&
        capFlags?.delete &&
        !STAVBY_DELETE_FORBIDDEN_SECTION_IDS.includes(id),
    );
    sections[id] = { view, edit, delete: remove };
  }

  const allowedView = new Set(ceiling.organizations.viewCompanyIds);
  const allowedManage = new Set(ceiling.organizations.manageCompanyIds);
  const viewCompanyIds = requested.organizations.viewCompanyIds.filter((cid) => allowedView.has(cid));
  const manageCompanyIds = requested.organizations.manageCompanyIds.filter((cid) =>
    allowedManage.has(cid),
  );

  return {
    sections,
    organizations: {
      viewCompanyIds:
        viewCompanyIds.length > 0 ? viewCompanyIds : [...ceiling.organizations.viewCompanyIds],
      manageCompanyIds,
      viewAllCompanies: Boolean(
        requested.organizations.viewAllCompanies && ceiling.organizations.viewAllCompanies,
      ),
      manageAllCompanies: Boolean(
        requested.organizations.manageAllCompanies && ceiling.organizations.manageAllCompanies,
      ),
    },
  };
}

